import { motion } from 'framer-motion'
import { forwardRef, useRef } from 'react'

import { pageHasExportableImage } from '../../lib/comicPageExport'
import { pageShowsCandidatePicker, pageShowsGeneratingOnCanvas } from '../../lib/comicPageImageStatus'
import type { ComicPageLayoutTemplate } from '../../lib/comicPageLayout'
import { getPageLayoutOption, wholePagePreviewAspectClass } from '../../lib/comicPageLayout'
import {
  comicCanvasPagePreviewIssueDetail,
  isComicCanvasPageLayoutEmpty,
  resolveComicCanvasPagePreviewIssue,
  type ComicCanvasPagePreviewIssue,
} from '../../lib/comicCanvasPreviewState'
import { getPanelsOnPage } from '../../lib/comicPages'
import {
  clampCandidatesPerGeneration,
  type ComicPanelAspectPreset,
  panelAspectTailwindClass,
} from '../../lib/comicProjectSettings'
import type { ComicEpisode, ComicPage, ComicPanel } from '../../lib/comicTypes'
import { mediaDisplayUrl } from '../../lib/resolvedMediaImage'
import { chromeSelectableMediaCardClass } from '../ChromeSurface'
import { ThemedImage } from '../ThemedImage'
import { ThemedImagePlaceholder } from '../ThemedImagePlaceholder'
import { ComicPageLayoutSkeletonOverlay } from './ComicPageLayoutSkeletonOverlay'
import { comicPanelRound, comicSectionLabelClass } from './comicPanelTokens'
import { ComicRailExportButton } from './ComicRailSection'



type Props = {

  angular: boolean

  episode: ComicEpisode

  page: ComicPage

  panelAspect: ComicPanelAspectPreset

  candidatesPerGeneration: number

  selectedPanelId: string | null

  selectedPageId: string | null

  onSelectPage: (pageId: string) => void

  onSelectPanel: (panelId: string) => void

  onOpenCandidates: (panelId: string) => void

  onOpenPageCandidates?: (pageId: string) => void

  onExportPage?: (pageId: string) => void
  onEditPageImage?: (pageId: string) => void

  exportDisabled?: boolean

  /** 编辑器内正在请求 creation，尚未写入 pageImageStatus */
  generatingPageId?: string | null

  /** 点击画布成图进入灯箱（与候选弹窗共用 ImageLightbox） */
  onOpenImagePreview?: (payload: {
    pageId: string
    imageUrl: string
    panelId: string | null
    scope: 'page' | 'panel'
  }) => void

}



function resolvePreviewPanel(panels: ComicPanel[], selectedPanelId: string | null): ComicPanel | null {

  return (

    panels.find((p) => p.id === selectedPanelId) ??

    panels.find((p) => p.script.trim()) ??

    panels[0] ??

    null

  )

}



function previewColumnSources(

  page: ComicPage,

  panel: ComicPanel | null,

  columnCount: number,

): (string | null)[] {

  const pageImage = page.pageImageUrl?.trim() ? mediaDisplayUrl(page.pageImageUrl) : null

  if (pageImage) {

    return Array.from({ length: columnCount }, (_, i) => (i === 0 ? pageImage : null))

  }

  const pageCandUrls = page.pageCandidates
    .map((c) => (c.imageUrl?.trim() ? mediaDisplayUrl(c.imageUrl) : null))
    .filter(Boolean) as string[]

  if (pageCandUrls.length > 0) {

    return Array.from(

      { length: columnCount },

      (_, i) => pageCandUrls[i] ?? null,

    )

  }

  if (!panel) return Array.from({ length: columnCount }, () => null)

  const urls = panel.candidates
    .map((c) => (c.imageUrl?.trim() ? mediaDisplayUrl(c.imageUrl) : null))
    .filter(Boolean) as string[]

  const panelMain = panel.imageUrl?.trim() ? mediaDisplayUrl(panel.imageUrl) : null

  return Array.from({ length: columnCount }, (_, i) => urls[i] ?? (i === 0 ? panelMain : null))

}



const previewColGridClass: Record<number, string> = {

  1: 'grid-cols-1',

  2: 'grid-cols-2',

  3: 'grid-cols-3',

  4: 'grid-cols-4',

}

function resolveWholePagePreviewUrl(page: ComicPage): string | null {
  const applied = page.pageImageUrl?.trim()
  if (applied) return mediaDisplayUrl(applied)
  const first = page.pageCandidates.find((c) => c.imageUrl?.trim())
  return first?.imageUrl?.trim() ? mediaDisplayUrl(first.imageUrl) : null
}



export const ComicPagePreviewCard = forwardRef<HTMLElement, Props>(function ComicPagePreviewCard(

  {

    angular,

    episode,

    page,

    panelAspect,

    candidatesPerGeneration,

    selectedPanelId,

    selectedPageId,

    onSelectPage,

    onSelectPanel,

    onOpenCandidates,

    onOpenPageCandidates,

    onExportPage,

    onEditPageImage,

    exportDisabled = false,

    generatingPageId = null,

    onOpenImagePreview,

  },

  ref,

) {

  const layoutOpt = getPageLayoutOption(page.layout)

  const panels = getPanelsOnPage(episode, page)

  const previewPanel = resolvePreviewPanel(panels, selectedPanelId)

  const pageCandCount = page.pageCandidates.length

  const genPerRequest = clampCandidatesPerGeneration(candidatesPerGeneration)

  const columnCount = Math.max(
    1,
    Math.min(4, pageCandCount > 1 ? pageCandCount : genPerRequest),
  )

  const columnSources = previewColumnSources(page, previewPanel, columnCount)

  const pageSelected = selectedPageId === page.id

  const pageImageUrl = page.pageImageUrl ?? null

  const wholePagePreviewUrl = resolveWholePagePreviewUrl(page)

  const showWholePagePreview =
    Boolean(wholePagePreviewUrl) &&
    (Boolean(pageImageUrl) || page.pageCandidates.length <= 1)

  const pageGenerating =
    generatingPageId === page.id || pageShowsGeneratingOnCanvas(page)

  const previewIssue = resolveComicCanvasPagePreviewIssue(panelAspect, episode, page)
  const layoutEmpty = isComicCanvasPageLayoutEmpty(page, episode)
  const wholePageAspect = wholePagePreviewAspectClass(page.layout)
  const aspectClass = panelAspectTailwindClass(panelAspect)

  const colGrid = previewColGridClass[columnCount] ?? 'grid-cols-2'

  const candCount = previewPanel?.candidates.length ?? 0

  const needsPagePick = pageShowsCandidatePicker(
    page.pageImageStatus,
    pageCandCount,
    Boolean(page.pageImageUrl),
  )

  const showPageCandidatesAction = pageCandCount > 0 && Boolean(onOpenPageCandidates)

  const canExport = pageHasExportableImage(episode, page)
  const canEditPageImage = Boolean(onEditPageImage && wholePagePreviewUrl && !pageGenerating)



  return (

    <motion.article

      ref={ref}

      id={`comic-canvas-page-${page.id}`}

      layout

      initial={{ opacity: 0, y: 8 }}

      animate={{ opacity: 1, y: 0 }}

      className={[

        'scroll-mt-4 border-b border-line/70 bg-transparent py-4 transition-colors',

        pageSelected

          ? 'border-accent bg-[color-mix(in_srgb,var(--accent)_5%,transparent)]'

          : '',

      ].join(' ')}

    >

      <div className="mb-3 flex min-w-0 items-center justify-between gap-2">

        <button

          type="button"

          onClick={() => onSelectPage(page.id)}

          className="min-w-0 flex-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"

        >

          <p className={comicSectionLabelClass()}>{page.title}</p>

          <p className="mt-0.5 text-xs text-ink-soft">

            {previewIssue
              ? '版式异常'
              : pageImageUrl
              ? '整页成图'
              : pageCandCount > 0
                ? `整页候选 · ${pageCandCount} 张`
                : layoutEmpty
                  ? `${panels.length}/${layoutOpt.maxPanels} 格 · 版式骨架`
                  : `${panels.length}/${layoutOpt.maxPanels} 格 · ${columnCount} 列预览`}

          </p>

        </button>

        {showPageCandidatesAction ? (
          <button
            type="button"
            className={[
              'shrink-0 text-xs font-semibold underline-offset-2 hover:underline',
              needsPagePick ? 'text-accent animate-pulse' : 'text-ink-soft',
            ].join(' ')}
            onClick={() => onOpenPageCandidates?.(page.id)}
          >
            {needsPagePick
              ? `选择整页（${pageCandCount} 张）`
              : `查看候选（${pageCandCount}）`}
          </button>
        ) : null}

        <span className="shrink-0 text-xs font-semibold text-ink-soft">{layoutOpt.label}</span>

        {canEditPageImage ? (
          <button
            type="button"
            className="shrink-0 text-xs font-semibold text-ink-soft underline-offset-2 hover:text-ink hover:underline"
            onClick={() => onEditPageImage?.(page.id)}
          >
            改图修字
          </button>
        ) : null}

        {onExportPage ? (

          <ComicRailExportButton

            angular={angular}

            label={`导出${page.title}`}

            tooltip="导出本页 PNG"

            disabled={exportDisabled || !canExport}

            onClick={() => onExportPage(page.id)}

          />

        ) : null}

      </div>



      {page.pageImageStatus === 'failed' && page.pageErrorMessage ? (

        <p

          className={[

            'mb-3 rounded-[var(--radius-sm)] border border-line bg-surface-muted px-3 py-2 text-xs text-ink',

            comicPanelRound(angular),

          ].join(' ')}

          role="alert"

        >

          {page.pageErrorMessage}

        </p>

      ) : null}



      {showWholePagePreview && wholePagePreviewUrl ? (
        <WholePagePreviewMedia
          angular={angular}
          src={wholePagePreviewUrl}
          aspectClass={wholePageAspect}
          selected={pageSelected}
          generating={pageGenerating}
          onActivate={() => {
            onSelectPage(page.id)
            if (previewPanel) onSelectPanel(previewPanel.id)
          }}
          onOpenImagePreview={
            onOpenImagePreview
              ? () =>
                  onOpenImagePreview({
                    pageId: page.id,
                    imageUrl: wholePagePreviewUrl,
                    panelId: previewPanel?.id ?? null,
                    scope: 'page',
                  })
              : undefined
          }
        />
      ) : previewIssue ? (
        <ComicPageLayoutAnomaly
          angular={angular}
          layout={page.layout}
          issue={previewIssue}
          selected={pageSelected}
          onActivate={() => onSelectPage(page.id)}
        />
      ) : (
        <div className={['grid min-w-0 gap-2 sm:gap-3', colGrid].join(' ')}>
          {columnSources.map((src, colIndex) => (
            <PagePreviewColumn
              key={colIndex}
              angular={angular}
              aspectClass={aspectClass}
              layout={page.layout}
              columnIndex={colIndex}
              imageUrl={src}
              selected={previewPanel?.id === selectedPanelId}
              generating={pageGenerating && colIndex === 0 && !src}
              onActivate={() => {
                onSelectPage(page.id)
                if (previewPanel) onSelectPanel(previewPanel.id)
              }}
              onOpenImagePreview={
                onOpenImagePreview && src
                  ? () =>
                      onOpenImagePreview({
                        pageId: page.id,
                        imageUrl: src,
                        panelId: previewPanel?.id ?? null,
                        scope: 'panel',
                      })
                  : undefined
              }
              onOpenCandidates={
                previewPanel && candCount > 0 ? () => onOpenCandidates(previewPanel.id) : undefined
              }
              showCandidatesAction={colIndex === 0 && candCount > 0}
              candidatesLabel={candCount > 1 ? `${candCount} 张候选` : '查看成图'}
            />
          ))}
        </div>
      )}

    </motion.article>

  )

})

/** 整页成图/单张候选：与候选弹窗、图库共用 chromeSelectableMediaCard + ThemedImage */
function WholePagePreviewMedia({
  angular,
  src,
  aspectClass,
  selected,
  generating,
  onActivate,
  onOpenImagePreview,
}: {
  angular: boolean
  src: string
  aspectClass: string
  selected: boolean
  generating: boolean
  onActivate: () => void
  onOpenImagePreview?: () => void
}) {
  const activate = () => {
    onOpenImagePreview?.()
    onActivate()
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          activate()
        }
      }}
      className={[
        chromeSelectableMediaCardClass(angular, selected),
        aspectClass,
        'block w-full min-w-0 cursor-pointer bg-[color-mix(in_srgb,var(--ink)_8%,var(--surface-muted))] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
      ].join(' ')}
      aria-label={onOpenImagePreview ? '放大预览整页成图' : '整页成图预览'}
    >
      <ThemedImage
        src={src}
        alt=""
        angular={angular}
        compact
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover object-center"
        fallbackClassName="flex h-full min-h-[12rem] w-full items-center justify-center bg-surface-muted"
      />
      {generating ? (
        <div className="absolute inset-0 z-[2] flex items-center justify-center bg-[color-mix(in_srgb,var(--panel)_82%,transparent)] text-xs font-semibold text-ink">
          生成中…
        </div>
      ) : null}
    </div>
  )
}

function ComicPageLayoutAnomaly({
  angular,
  layout,
  issue,
  selected,
  onActivate,
}: {
  angular: boolean
  layout: ComicPageLayoutTemplate
  issue: ComicCanvasPagePreviewIssue
  selected: boolean
  onActivate: () => void
}) {
  return (
    <ThemedImagePlaceholder
      as="button"
      angular={angular}
      aspectClass={wholePagePreviewAspectClass(layout)}
      loading={false}
      selected={selected}
      onClick={onActivate}
      aria-label="版式异常"
    >
      <div
        className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-1.5 px-4 text-center"
        role="alert"
      >
        <p className="text-sm font-semibold text-accent">版式异常</p>
        <p className="text-xs leading-relaxed text-ink-soft">
          {comicCanvasPagePreviewIssueDetail(issue)}
        </p>
      </div>
    </ThemedImagePlaceholder>
  )
}

function PagePreviewColumn({

  angular,

  aspectClass,

  layout,

  columnIndex,

  imageUrl,

  selected,

  generating,

  onActivate,

  onOpenImagePreview,

  onOpenCandidates,

  showCandidatesAction,

  candidatesLabel,

}: {

  angular: boolean

  aspectClass: string

  layout: ComicPageLayoutTemplate

  columnIndex: number

  imageUrl: string | null

  selected: boolean

  generating: boolean

  onActivate: () => void

  onOpenImagePreview?: () => void

  onOpenCandidates?: () => void

  showCandidatesAction?: boolean

  candidatesLabel?: string

}) {

  const previewContainerRef = useRef<HTMLButtonElement>(null)

  const hasImage = Boolean(imageUrl)

  return (

    <div className="flex min-w-0 flex-col gap-1.5">

      <ThemedImagePlaceholder

        ref={columnIndex === 0 ? previewContainerRef : undefined}

        as="button"

        angular={angular}

        aspectClass={aspectClass}

        loading={!hasImage}

        selected={selected}

        onClick={() => {
          if (imageUrl && onOpenImagePreview) onOpenImagePreview()
          onActivate()
        }}

        aria-label={imageUrl && onOpenImagePreview ? `放大预览位 ${columnIndex + 1}` : `预览位 ${columnIndex + 1}`}

      >

        {imageUrl ? (
          <ThemedImage
            src={imageUrl}
            alt=""
            angular={angular}
            compact
            loading="lazy"
            decoding="async"
            className="absolute inset-0 z-[1] h-full w-full object-cover"
            fallbackClassName="absolute inset-0 z-[1] flex h-full w-full items-center justify-center bg-surface-muted"
          />
        ) : null}

        {!hasImage && !generating ? (
          <ComicPageLayoutSkeletonOverlay layout={layout} faint={false} className="z-[1]" />
        ) : null}

        {generating ? (
          <div className="absolute inset-0 z-[3] flex items-center justify-center bg-[color-mix(in_srgb,var(--panel)_82%,transparent)] text-xs font-semibold text-ink">
            生成中…
          </div>
        ) : null}

        <span className="pointer-events-none absolute bottom-1.5 right-1.5 z-[4] text-[9px] font-bold tabular-nums text-ink-soft">

          {columnIndex + 1}

        </span>

      </ThemedImagePlaceholder>

      {showCandidatesAction && onOpenCandidates ? (

        <button

          type="button"

          onClick={onOpenCandidates}

          className={[

            'border border-line px-2 py-1 text-center text-[10px] font-semibold text-accent hover:border-accent',

            angular ? 'rounded-none' : 'rounded-md',

          ].join(' ')}

        >

          {candidatesLabel}

        </button>

      ) : null}

    </div>

  )

}


